import React, { useState } from 'react';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';

const WithdrawModal = ({ currency, onClose, onWithdraw }) => {
  const [amount, setAmount] = useState('');
  const [address, setAddress] = useState('');
  const [selectedNetwork, setSelectedNetwork] = useState(currency?.network || 'Bitcoin');
  const [memo, setMemo] = useState('');
  const [errors, setErrors] = useState({});

  if (!currency) return null;

  const networks = currency.isFiat 
    ? [
        { id: 'ach', name: 'ACH Bank Transfer', fee: 0, time: '1-3 business days' },
        { id: 'wire', name: 'Wire Transfer', fee: 25, time: '1 business day' }
      ]
    : [
        { id: 'mainnet', name: currency.network, fee: currency.withdrawalFee, time: '10-30 minutes' }
      ];

  const calculateTotal = () => {
    const withdrawAmount = parseFloat(amount) || 0;
    const networkFee = currency.isFiat 
      ? networks.find(n => n.name === selectedNetwork)?.fee || 0
      : currency.withdrawalFee;
    return withdrawAmount + networkFee;
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!amount || parseFloat(amount) <= 0) {
      newErrors.amount = 'Please enter a valid amount';
    } else if (parseFloat(amount) < currency.minWithdrawal) {
      newErrors.amount = `Minimum withdrawal is ${currency.minWithdrawal} ${currency.symbol}`;
    } else if (calculateTotal() > currency.balance) {
      newErrors.amount = 'Insufficient balance including fees';
    }

    if (!currency.isFiat && !address) {
      newErrors.address = 'Please enter a withdrawal address';
    } else if (!currency.isFiat && address.length < 26) {
      newErrors.address = 'Please enter a valid address';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onWithdraw(currency.symbol, parseFloat(amount), address);
    }
  };

  const handleMaxAmount = () => {
    const maxAmount = currency.balance - (currency.isFiat ? 0 : currency.withdrawalFee);
    setAmount(Math.max(0, maxAmount).toString());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-modal p-4">
      <div className="bg-surface rounded-trading border border-border w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <Image
              src={currency.icon}
              alt={currency.name}
              className="w-8 h-8 rounded-full"
            />
            <div>
              <h2 className="text-lg font-heading-semibold text-text-primary">
                Withdraw {currency.symbol}
              </h2>
              <p className="text-sm text-text-secondary">
                Available: {currency.balance.toLocaleString()} {currency.symbol}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-trading hover:bg-secondary-50 transition-colors duration-trading-fast"
          >
            <Icon name="X" size={20} className="text-text-secondary" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Network Selection */}
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-3">
              Select Network
            </label>
            <div className="space-y-2">
              {networks.map((network) => (
                <button
                  key={network.id}
                  type="button"
                  onClick={() => setSelectedNetwork(network.name)}
                  className={`w-full flex items-center justify-between p-3 border rounded-trading transition-all duration-trading-fast ${
                    selectedNetwork === network.name
                      ? 'border-primary bg-primary-50' :'border-border hover:border-secondary-300'
                  }`}
                >
                  <div className="text-left">
                    <p className="font-body-medium text-text-primary">{network.name}</p>
                    <p className="text-sm text-text-secondary">
                      Fee: {currency.isFiat ? `$${network.fee}` : `${network.fee} ${currency.symbol}`} • 
                      Time: {network.time}
                    </p>
                  </div>
                  <div className={`w-4 h-4 rounded-full border-2 ${
                    selectedNetwork === network.name
                      ? 'border-primary bg-primary' :'border-secondary-300'
                  }`}>
                    {selectedNetwork === network.name && (
                      <div className="w-2 h-2 bg-white rounded-full m-0.5"></div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Withdrawal Address */}
          {!currency.isFiat && (
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-2">
                Withdrawal Address *
              </label>
              <input
                type="text"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={`Enter ${currency.symbol} address`}
                className={`w-full px-3 py-2 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.address ? 'border-error' : 'border-border'
                }`}
              />
              {errors.address && (
                <p className="mt-1 text-sm text-error">{errors.address}</p>
              )}
            </div>
          )}

          {/* Amount */}
          <div>
            <label className="block text-sm font-body-medium text-text-secondary mb-2">
              Amount *
            </label>
            <div className="relative">
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="0.00"
                step="any"
                min={currency.minWithdrawal}
                max={currency.balance}
                className={`w-full px-3 py-2 pr-20 border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent ${
                  errors.amount ? 'border-error' : 'border-border'
                }`}
              />
              <div className="absolute right-3 top-2 flex items-center space-x-2">
                <button
                  type="button"
                  onClick={handleMaxAmount}
                  className="text-xs text-primary hover:text-primary-600 font-body-medium"
                >
                  MAX
                </button>
                <span className="text-sm text-text-secondary">{currency.symbol}</span>
              </div>
            </div>
            {errors.amount && (
              <p className="mt-1 text-sm text-error">{errors.amount}</p>
            )}
          </div>

          {/* Memo (for certain currencies) */}
          {['XRP', 'XLM', 'EOS'].includes(currency.symbol) && (
            <div>
              <label className="block text-sm font-body-medium text-text-secondary mb-2">
                Memo/Tag (Optional)
              </label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Enter memo or destination tag"
                className="w-full px-3 py-2 border border-border rounded-trading focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              />
            </div>
          )}

          {/* Transaction Summary */}
          <div className="p-4 bg-secondary-50 rounded-trading space-y-2">
            <h3 className="font-body-medium text-text-primary mb-3">Transaction Summary</h3>
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">Withdrawal Amount</span>
              <span className="text-text-primary">
                {amount || '0'} {currency.symbol}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-text-secondary">Network Fee</span>
              <span className="text-text-primary">
                {currency.isFiat 
                  ? `$${networks.find(n => n.name === selectedNetwork)?.fee || 0}`
                  : `${currency.withdrawalFee} ${currency.symbol}`
                }
              </span>
            </div>
            <div className="flex justify-between text-sm pt-2 border-t border-border">
              <span className="font-body-medium text-text-primary">Total Deducted</span>
              <span className="font-body-medium text-text-primary">
                {calculateTotal().toFixed(currency.isFiat ? 2 : 8)} {currency.symbol}
              </span>
            </div>
          </div>

          {/* Security Notice */}
          <div className="p-4 bg-warning-50 border border-warning-200 rounded-trading">
            <div className="flex items-start space-x-3">
              <Icon name="Shield" size={20} className="text-warning mt-0.5" />
              <div>
                <p className="font-body-medium text-warning-700 mb-1">Security Notice</p>
                <p className="text-sm text-warning-600">
                  {parseFloat(amount) > 1000 
                    ? 'This withdrawal requires 2FA verification due to the amount.'
                    : 'Double-check all details before confirming. Transactions cannot be reversed.'
                  }
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-trading font-body-medium text-text-primary hover:bg-secondary-50 transition-colors duration-trading-fast"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 bg-primary text-white px-4 py-2 rounded-trading font-body-medium hover:bg-primary-600 transition-colors duration-trading-fast"
            >
              Withdraw
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WithdrawModal;